import { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut, User } from 'firebase/auth';
import { auth, googleProvider } from './firebase';

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

/**
 * Auth hook. Sign-in is Google-only; the blocking function rejects non-allowlisted
 * accounts, surfaced here as an error so the sign-in screen can explain it.
 */
export function useAuth(): AuthState & { signIn: () => void; signOutUser: () => void } {
  const [state, setState] = useState<AuthState>({ user: null, loading: true, error: null });

  useEffect(() => onAuthStateChanged(auth, (user) => setState({ user, loading: false, error: null })), []);

  const signIn = async () => {
    setState((s) => ({ ...s, error: null }));
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      const msg =
        e instanceof Error && /allowlist|permission|blocked|denied/i.test(e.message)
          ? 'This account is not on the access allowlist.'
          : 'Sign-in failed. Only Rocketable Google accounts are permitted.';
      setState((s) => ({ ...s, error: msg }));
    }
  };

  const signOutUser = () => void signOut(auth);

  return { ...state, signIn, signOutUser };
}
